import qs from "query-string";
import {
    ISearch,
    IOptionSearch,
    IFareAvail,
    ITourFareBookingTime,
    IAvailabilityByProductAndRange,
    IAvailabilityByRange,
    IAvailabilityDetailsToConfirm,
    AvailabilityType,
    ICheckAvailabilityProduct,
} from "@interfaces";
import { diffDate, filterProducts } from "@utilities";
import ftApiClient from "./ftApiClient";
import wtApiClient from "./wtApiClient";
import { cacheApi } from "./cacheApi";
import moment from "moment";
import { CHECK_AVAILABILITY_START_DATE_FORMAT } from "@constants";
import _ from "lodash";

const availabilityDetailsCache = new Map<string, Promise<IAvailabilityDetailsToConfirm>>();

export const toursApi = {
    search: (searches: ISearch, option?: IOptionSearch) => {
        const hasRegionsAndCountries = searches.regions && searches.countries;
        let queryString = qs.stringify(searches) ? qs.stringify(searches) : "all=\\s";

        if (!hasRegionsAndCountries) {
            if (searches.day !== undefined && _.keys(searches).length === 1) {
                queryString = qs.stringify(searches) + "&all=\\s";
            } else if (searches.sortDirection && _.keys(searches).length === 2) {
                // if only sortDirection
                queryString = qs.stringify(searches) + "&all=\\s";
            }
        }

        const optionQuery = option ? "&" + qs.stringify(option) : "";

        // Added sortby=ranking to every search query
        const url = "/products?" + queryString + optionQuery + "&sortDirection=asc&sortBy=ranking";

        return wtApiClient.get(url).then((response) => {
            if (response.data?.results) {
                response.data.results = filterProducts(response.data.results);
            }
            return response;
        });
    },

    getById: async (productId: number | string) => {
        try {
            const cacheResp = await cacheApi.readProduct(productId);
            const product = cacheResp.data?.data;
            if (product && product.productId && product.name) {
                const results = filterProducts([product]);
                return { ...cacheResp, data: { results } };
            }
        } catch {}
        const url = "/product/" + productId;
        return wtApiClient.get(url).then((response) => {
            if (response.data?.results) {
                response.data.results = filterProducts(response.data.results);
            }
            return response;
        });
    },
    getMediasById: (productId: number | string) => {
        const url = "/media/images/product/" + productId;
        return wtApiClient.get(url);
    },
    getBookingDetail: async (productPricesDetailsId: number | string) => {
        try {
            const cacheResp = await cacheApi.readBookingDetails(productPricesDetailsId);
            const bookingData = cacheResp.data?.data;
            if (bookingData && Array.isArray(bookingData.bookingTimes)) {
                return { ...cacheResp, data: bookingData } as { data: ITourFareBookingTime } & typeof cacheResp;
            }
        } catch {}
        const url = "/bookingdetails/" + productPricesDetailsId;
        return wtApiClient.get<ITourFareBookingTime>(url);
    },
    checkAvail: (params: IFareAvail) => {
        const startDate = new Date(params.from || new Date()).getDate();
        const startMonth = new Date(params.from || new Date()).toLocaleString("en-us", {
            month: "short",
        });
        const startYear = new Date(params.from || new Date()).getFullYear();
        const dateCount = diffDate(params.from || new Date(), params.to || new Date());

        const url = `/checkavailabilityrange/${params.productPricesDetailsId}/${
            params.bookingTime
        }/${startDate}-${startMonth}-${startYear}/${dateCount + 1}`;
        return wtApiClient.get(url, { params: { cached: 1 } });
    },
    checkAvailRange: (ppdid: string, timeId: string, startDate: Date, days: number) => {
        const url = `/checkavailabilityrange/${ppdid}/${timeId}/${moment(startDate).format(CHECK_AVAILABILITY_START_DATE_FORMAT)}/${days}`;
        return wtApiClient.get<IAvailabilityByRange[]>(url, { params: { cached: 1 } }).then((resp) => {
            return resp.data.map((a) => ({ ...a, availabilityType: AvailabilityType.ByRange }));
        });
    },
    checkAvailabilityProduct: (productId: string | number, startDate: Date, days: number) => {
        const from = moment(startDate).format(CHECK_AVAILABILITY_START_DATE_FORMAT).toUpperCase();
        const url = "/checkAvailabilityProduct";
        return wtApiClient
            .get<ICheckAvailabilityProduct>(url, { params: { productId, from, days } })
            .then((resp) => resp.data);
    },
    checkAvailByProductAndRange: (
        productId: string,
        fareTypeId: string,
        startDate: Date,
        endDate: Date,
    ) => {
        const formattedStartDate = moment(startDate).format(CHECK_AVAILABILITY_START_DATE_FORMAT);
        const formattedEndDate = moment(endDate).format(CHECK_AVAILABILITY_START_DATE_FORMAT);
        const url = `/checkAvailabilityByProductAndRange/${productId}/${fareTypeId}/${formattedStartDate}/${formattedEndDate}`;
        return wtApiClient.post<IAvailabilityByProductAndRange[]>(url, null, { params: { cached: 1 } }).then((resp) => {
            return resp.data.map((a) => ({
                ...a,
                availabilityType: AvailabilityType.ByProductAndRange,
            }));
        });
    },
    getDetailsToConfirmAvailability: (ppid: string, date: Date, timeId: string) => {
        const url = `/bookingdetails/detailsToConfirmAvailability/${ppid}/${moment(date).format(CHECK_AVAILABILITY_START_DATE_FORMAT)}/${timeId}`;
        if (availabilityDetailsCache.has(url)) {
            return availabilityDetailsCache.get(url)!;
        }
        const promise = wtApiClient
            .get<IAvailabilityDetailsToConfirm>(url)
            .then((response) => response.data);
        availabilityDetailsCache.set(url, promise);
        return promise;
    },

    getFavouriteTour: () => {
        const url = "/favourite/products/all";
        return ftApiClient.get(url).then((response) => {
            if (response.data?.data) {
                response.data.data = filterProducts(response.data.data);
            }
            return response;
        });
    },

    addToFavourite: (productId: string | number) => {
        const url = `/favourite/products/add/${productId}`;
        return ftApiClient.post(url);
    },
    deleteFavourite: (productId: string | number) => {
        const url = `/favourite/products/remove/${productId}`;
        return ftApiClient.delete(url);
    },
};
